import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { TreePine } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Welcome to LAVista!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16 bg-secondary">
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 shadow-sm" data-testid="register-page">
        <div className="flex items-center gap-2 mb-2 text-primary">
          <TreePine className="h-6 w-6 text-accent" strokeWidth={1.5} />
          <span className="font-heading text-2xl font-semibold">LAVista Stays<span className="text-accent">.com</span></span>
        </div>
        <h1 className="font-heading text-3xl font-light mt-4">Create your account</h1>
        <p className="text-muted-foreground text-sm mt-1">Track your inquiries and services.</p>

        <form onSubmit={submit} className="space-y-5 mt-7">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" data-testid="register-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 ..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="Min. 6 characters" />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
          <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full rounded-full h-11">
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-to-login-link" className="text-accent font-semibold hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
